import { Subject } from 'rxjs';
import { asap } from 'rxjs/scheduler/asap';
import { MyNavigator } from 'typings/globals/cordova';
import {
  PermissionStateEnum,
  keepLocationPermission,
  polyfill,
} from 'utils/permissions.request';

import {
  LocationPermissions,
  PermissionProvider,
  Permissions,
} from './Permissions';

declare const navigator: MyNavigator;

// polyfills the browser permissions request functions
polyfill();

export class BrowserPermissions implements PermissionProvider {
  private permissions$Internal: Subject<Permissions> = new Subject();
  readonly permissions$ = this.permissions$Internal.shareReplay(1, null, asap);

  public requestLocationBackgroundPermission(): Promise<boolean> {
    return this.requestLocationInUsePermission();
  }

  public askForLocationPermissionsAfterDenial(): Promise<boolean> {
    return this.requestLocationInUsePermission();
  }

  public requestLocationInUsePermission(): Promise<boolean> {
    if (navigator.geolocation.getCurrentPosition) {
      return new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, {}),
      )
        .then(() => {
          keepLocationPermission();
          setTimeout(() => this.permissions(), 200);
          return this.hasLocationPermission();
        })
        .then(({ inUse }) => inUse)
        .catch((e) => {
          keepLocationPermission(e);
          return false;
        });
    }
    return Promise.resolve(false);
  }

  public requestHighAccuracyLocationPermission(): Promise<boolean> {
    return Promise.resolve(true);
  }

  public permissions(): Promise<Permissions> {
    return Promise.all([
      this.hasLocationPermission(),
      this.hasPush(),
      this.hasCamera(),
    ])
      .then(([location, push, camera]) => ({
        location,
        push,
        camera,
        backgroundRefresh: true,
      }))
      .then((p) => {
        this.permissions$Internal.next(p);
        return p;
      });
  }

  hasPush(): Promise<boolean> {
    return navigator.permissions
      .query({ name: 'push' })
      .then((result) => result.state === PermissionStateEnum.GRANTED)
      .catch(() => false);
  }

  hasCamera(): Promise<boolean> {
    return navigator.permissions
      .query({ name: 'camera' })
      .then((result) => result.state === PermissionStateEnum.GRANTED)
      .catch(() => false);
  }

  hasLocationPermission(): Promise<LocationPermissions> {
    return navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => ({
        inUse: result.state === PermissionStateEnum.GRANTED,
        gpsEnabled: result.state === PermissionStateEnum.GRANTED,
        getHighAccuracyPermission: result.state === PermissionStateEnum.GRANTED,
        background: false,
        canRequestBackground: false,
        canRequestInUse: result.state === PermissionStateEnum.PROMPT,
        canReRequest: false,
      }))
      .catch(() => ({
        inUse: false,
        gpsEnabled: false,
        getHighAccuracyPermission: false,
        background: false,
        canRequestBackground: false,
        canRequestInUse: false,
        canReRequest: false,
      }));
  }

  constructor() {
    this.permissions();
  }
}
